import { Component } from '@angular/core';
import { OnInit, OnDestroy, Input } from '@angular/core';
import { ElementRef, Renderer2, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertsService } from '../../services/alerts.service';
import { UserService } from '../../services/user.service';

/**
 * Component to print a cropped product area.
 */
@Component({
  selector: 'print-area',
  templateUrl: './print-area.component.html',
  styleUrl: './print-area.component.css'
})
export class PrintAreaComponent implements OnInit, OnDestroy {
    @Input() product: any;

	@ViewChild('region') region!: ElementRef;
	@ViewChild('regionContainer') regionContainer!: ElementRef;
	@ViewChild('screenshot') screenshot!: ElementRef;
	@ViewChild('slice') slice!: ElementRef;
    
    private clickListener: () => void = () => {};
    private moveListener: () => void = () => {};

    LOCALIZATION: any;
    
    coords = {
      sx: -1,
      sy: -1,
      ex: -1,
      ey: -1,
      px: -1,
      py: -1,
      scalew: 1,
      scaleh: 1
    };
    start = 0;  
	
    loading: boolean = true;
	printSelectionHide: boolean = true;
	captureSelectionHide: boolean = false;
    
    constructor(
        private ngbmodalService: NgbActiveModal,
        private renderer: Renderer2,
        private userService: UserService,
        private alertsService: AlertsService
    ) { }
    
    ngOnInit(): void {
        this.LOCALIZATION = this.userService.LOCALIZATION;
    }
    
    ngOnDestroy(): void {
        if (this.clickListener) {
            this.clickListener();
        }
        if (this.moveListener) {
            this.moveListener();
        }
    }
        
    getBox(): void {
        const region = this.region.nativeElement;
        const container = this.regionContainer.nativeElement;
        const screenshot = this.screenshot.nativeElement;

        this.clickListener = this.renderer.listen(container, 'click', (e: MouseEvent) => {
            if (this.start >= 2) {
                this.start = 0;
            }
            if (this.start === 0) {
                // Calculate coordinates and update the region
                this.coords.sx = e.offsetX + screenshot.offsetLeft;
                this.coords.sy = e.offsetY + container.offsetTop;
                this.coords.ex = 0;
                this.coords.ey = 0;

                this.renderer.setStyle(region, 'top', `${this.coords.sy}px`);
                this.renderer.setStyle(region, 'left', `${this.coords.sx}px`);
                this.renderer.setStyle(region, 'height', '0');
                this.renderer.setStyle(region, 'width', '0');
                this.renderer.setStyle(region, 'border-width', '2px');
                
                const bounds = screenshot.getBoundingClientRect();
                const x = e.pageX - bounds.left;
                const y = e.pageY - bounds.top;
                const imgWidth = screenshot.naturalWidth;
                const imgHeight = screenshot.naturalHeight;

                this.coords.px = (x / screenshot.clientWidth) * imgWidth;
                this.coords.py = (y / screenshot.clientHeight) * imgHeight;
                this.coords.scalew = imgWidth / screenshot.clientWidth;
                this.coords.scaleh = imgHeight / screenshot.clientHeight;

                console.log(`Clicked at: (${this.coords.px}, ${this.coords.py})`);
            }
            this.start = (this.start + 1) % 3;
        });

        this.moveListener = this.renderer.listen(container, 'mousemove', (e: MouseEvent) => {
            if (this.start === 1) {
                // Update region size on mousemove
                this.coords.ex = e.offsetX + screenshot.offsetLeft - this.coords.sx;
                this.coords.ey = e.offsetY + container.offsetTop - this.coords.sy;

                this.renderer.setStyle(region, 'height', `${this.coords.ey}px`);
                this.renderer.setStyle(region, 'width', `${this.coords.ex}px`);
            }
        });
    }

    onBoxed(box: any) {
		this.printSelectionHide = false;
		this.captureSelectionHide = true;
		
		// Slice the image using the box coordinates, then print the slice
		// See https://developer.mozilla.org/en-US/docs/Web/API/CanvasRenderingContext2D/drawImage
		//
		// drawImage(image, sx, sy, sWidth, sHeight, dx, dy, dWidth, dHeight)
		// Given an image, this function takes the area of the source image specified by the rectangle whose
		// top-left corner is (sx, sy) and whose width and height are sWidth and sHeight and draws it into the
		// canvas, placing it on the canvas at (dx, dy) and scaling it to the size specified by dWidth and dHeight.		
		
        const canvas = this.slice.nativeElement as HTMLCanvasElement;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            const scale = 2;
            canvas.width = box.width * scale;
            canvas.height = box.height * scale;

            const img = this.screenshot.nativeElement as HTMLImageElement;
            ctx.drawImage(
                img,
                box.x,
                box.y,
                box.width,
                box.height,
                0,
                0,
                box.width * scale,
                box.height * scale
            );
        }
    }

    /**
     * Crops the section of the product enclosed by the given area.
     */
    cropArea(): void {
        console.log(this.coords);
        
        this.onBoxed({
          x: Math.floor(this.coords.px),
          y: Math.floor(this.coords.py),
          width: Math.floor(this.coords.ex * this.coords.scalew),
          height: Math.floor(this.coords.ey * this.coords.scaleh)
        });
    }
    
    /**
     * Prints the cropped area.
     */
    printSlice(): void {
        const dataUrl = (this.slice.nativeElement as HTMLCanvasElement).toDataURL();
        const printWin = window.open('', '_blank');
        if (printWin) {
          printWin.document.open();
          printWin.document.write(`
            <!DOCTYPE html>
            <html>
            <head><title>Print Cropped Area</title></head>
            <body onload="window.print();">
              <img src="${dataUrl}">
            </body>
            </html>
          `);
          printWin.document.close();
        }       
    }
    
    /**
     * Clears the cropped area.
     */
    resetSelection(): void {
		this.printSelectionHide = true;
		this.captureSelectionHide = false;
	   
        const canvas = this.slice.nativeElement as HTMLCanvasElement;
        const ctx = canvas.getContext('2d');
        ctx?.clearRect(0, 0, canvas.width, canvas.height);      
    }
    
    onLoaded(event: any): void {
        this.loading = false;
        this.getBox();
    }   
    
    onError(err: any): void {
        console.error('Image failed to load:', err);
        
        this.loading = false;
        this.alertsService.error({
            message: this.LOCALIZATION.printDocumentError,
            delay: 10000
        });
    }   
    
    /**
     * Closes the modal.
     */
    close(): void {
        this.ngbmodalService.dismiss();
    }   
}
